// Helper functions for bill printing
export const formatDate = (dateString) => {
  if (!dateString) return "";
  const cleanDate = dateString.replace(/^'/, "");
  return cleanDate;
};

// Process items to show consolidated groups as single rows
export const processBillItems = (items) => {
  if (!items || !Array.isArray(items)) return [];

  const processedItems = [];

  items.forEach((item, index) => {
    if (item.consolidated === true && item.items && Array.isArray(item.items)) {
      // For consolidated items - create one row with aggregated data
      let totalGrossWeight = 0;
      let totalLessWeight = 0;
      let totalNetWeight = 0;
      let totalAmount = 0;
      let totalFine = 0;
      let metalRate = item.metalRate || 0;
      let wastage = item.wastage || 0;
      let totalPercentage = item.totalPercentage || 0;
      let purity = item.purity || 0;
      let stoneCharges = item.stoneCharges || 0;
      let huidCharges = item.huidCharges || 0;
      let numberOfPieces = item.items.length;

      // Calculate aggregates from all sub-items
      item.items.forEach(subItem => {
        const grossWt = parseFloat(subItem.weight) || 0;
        const lessWt = parseFloat(subItem.lessWeight) || 0;
        const netWt = grossWt - lessWt;
        const subItemAmount = parseFloat(subItem.stonePrice) || 0;

        totalGrossWeight += grossWt;
        totalLessWeight += lessWt;
        totalNetWeight += netWt;
        totalAmount += subItemAmount;

        // Calculate fine if metal rate is 0
        if (metalRate === 0) {
          // const subPurity = parseFloat(subItem.melting) || purity;
          // const subWastage = parseFloat(subItem.makingCharges) || wastage;
          totalFine += netWt * ((totalPercentage) / 100);
        }
      });

      // Add stone charges and huid charges to total amount
      totalAmount += stoneCharges + huidCharges + item.totalAmount || 0;

      // If metal rate is specified, use the group's totalFine
      if (metalRate > 0) {
        totalFine = item.totalFine || 0;
      }

      processedItems.push({
        itemNumber: item.itemNumber,
        name: item.name,
        isConsolidated: true,
        numberOfPieces: numberOfPieces,
        grossWeight: totalGrossWeight,
        lessWeight: totalLessWeight,
        netWeight: totalNetWeight,
        metalRate: metalRate,
        wastage: wastage,
        totalPercentage: totalPercentage,
        making: item.making || 0,
        purity: purity,
        fine: totalFine,
        amount: totalAmount,
        originalTotalAmount: item.totalAmount,
        stoneCharges: stoneCharges,
        huidCharges: huidCharges
      });
    } else {
      // For non-consolidated items
      const amount = parseFloat(item.total) || 0;
      const grossWeight = parseFloat(item.weight) || 0;
      const lessWeight = parseFloat(item.lessWeight) || 0;
      const netWeight = grossWeight - lessWeight;
      const purity = parseFloat(item.melting) || 75;
      const wastage = parseFloat(item.wastage) || 0;
      const metalRate = parseFloat(item.metalRate) || 0;
      let fine = 0;

      if (metalRate === 0) {
        fine = netWeight * ((purity + wastage) / 100);
      }

      processedItems.push({
        itemNumber: item.itemNumber || (index + 1),
        name: item.name || item.itemName || 'Item',
        isConsolidated: false,
        numberOfPieces: 1,
        grossWeight: grossWeight,
        lessWeight: lessWeight,
        netWeight: netWeight,
        making: item.making || 0,
        metalRate: metalRate,
        wastage: wastage,
        purity: purity,
        fine: fine,
        amount: amount,
        stoneCharges: item.stoneCharges || 0,
        huidCharges: item.huidCharges || 0
      });
    }
  });

  return processedItems;
};

export const parseItemObject = (itemObject) => {
  if (!itemObject) return [];
  try {
    if (typeof itemObject === "string") {
      const parsed = JSON.parse(decodeURIComponent(itemObject));
      return processBillItems(parsed);
    }
    return processBillItems(itemObject);
  } catch (error) {
    console.warn("Could not parse item object:", error);
    return [];
  }
};

export const generateTransactionBillHTML = (transaction, customers) => {
  const customer = customers.find((c) => c.id === transaction.customer) || {};
  const isGst = transaction.transactionDetails?.gstApplicable;

  // Parse items properly - this will now return processed items (one row per consolidated group)
  const processedItems = parseItemObject(transaction.items);

  const billData = {
    type: isGst ? "gst" : "non-gst",
    data: {
      transactionDetails: {
        billNo: transaction.transactionDetails?.billNo || 1,
        gstApplicable: isGst,
      },
      date: transaction.date,
      calculatedTotals: {
        subtotal: transaction.calculatedTotals?.subtotal || 0,
      },
      paymentDetails: {
        cash: transaction.paymentDetails?.cash || 0,
        online: transaction.paymentDetails?.online || 0,
        cheque: transaction.paymentDetails?.cheque || 0,
        other: transaction.paymentDetails?.other || 0,
        advancePayment: {
          enabled: transaction.paymentDetails?.advancePayment?.enabled || false,
          total: transaction.paymentDetails?.advancePayment?.total || 0,
          breakup: transaction.paymentDetails?.advancePayment?.breakup || {},
        },
        discount: transaction.paymentDetails?.discount || 0,
      },
      metaDetails: {
        handledBy: transaction.metaDetails?.handledBy || "",
        gstNumber: transaction.metaDetails?.gstNumber || "",
        panNumber: transaction.metaDetails?.panNumber || "",
      },
      summary: {
        grandTotal: transaction.summary?.grandTotal || 0,
        pendingAmount: transaction.summary?.pendingAmount || 0,
        cgst: transaction.summary?.cgst || 0,
        sgst: transaction.summary?.sgst || 0,
      },
    },
    items: processedItems,
    payments: transaction.paymentDetails || {},
    customer: customer,
    advanceData: transaction.paymentDetails?.advancePayment?.breakup || {},
  };
  return generateBillHTML(billData);
};

export const getCarat = (melting) => {
  if (!melting) return "";
  switch (String(melting)) {
    case "92": return "22ct";
    case "75": return "18ct";
    case "76": return "18ct";
    case "58.5": return "14ct";
    case "50": return "12ct";
    case "999": return "24ct";
    default: return "";
  }
};

// Indian Number to Words Conversion
export const numberToWords = (num) => {
  if (typeof num !== "number") num = parseFloat(num) || 0;
  num = Math.floor(num);

  if (num === 0) return "Zero Rupees Only";

  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convertLessThanOneThousand = (n) => {
    if (n === 0) return "";
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result.trim();
  };

  const convertToIndianWords = (n) => {
    if (n === 0) return "";
    let crore = Math.floor(n / 10000000);
    n %= 10000000;
    let lakh = Math.floor(n / 100000);
    n %= 100000;
    let thousand = Math.floor(n / 1000);
    n %= 1000;
    let hundred = n;
    let result = "";
    if (crore > 0) result += convertLessThanOneThousand(crore) + " Crore ";
    if (lakh > 0) result += convertLessThanOneThousand(lakh) + " Lakh ";
    if (thousand > 0) result += convertLessThanOneThousand(thousand) + " Thousand ";
    if (hundred > 0) result += convertLessThanOneThousand(hundred);
    return result.trim();
  };

  const words = convertToIndianWords(num);
  return words ? words + " Rupees Only" : "Zero Rupees Only";
};

// Generate Non-GST Bill - Handles consolidated items as single rows
const generateNonGSTBill = (data, items, payments, customer, advanceData) => {
  let itemsHTML = "";
  // let totalGrossWeight = 0;
  let totalStoneCharge = 0;
  let totalHuidCharge = 0;
  // let totalLessWeight = 0;
  let totalNetWeight = 0;
  let totalFineGold = 0;
  let totalAmount = 0;
  let totalPieces = 0;
  let rowNumber = 1;

  items.forEach((item, index) => {
    // Add to totals
    totalPieces += item.numberOfPieces;
    // totalGrossWeight += item.grossWeight;
    totalStoneCharge += item.stoneCharges;
    totalHuidCharge += item.huidCharges;
    // totalLessWeight += item.lessWeight;
    totalNetWeight += item.netWeight;
    if (item.metalRate === 0) {
      totalFineGold += item.fine;
    }
    totalAmount += item.originalTotalAmount;

    // Format metal rate display
    const metalRateDisplay = item.metalRate > 0 ? item.metalRate.toFixed(2) : '-';

    // Format fine display - show 0 if metal rate is specified
    const fineDisplay = item.metalRate > 0 ? '0.000' : item.fine.toFixed(3);

    // Format purity display
    // const purityDisplay = item.purity > 0 ? item.purity.toFixed(3) : '-';
    console.log("Item:", item);
    itemsHTML += `
      <tr>
        <td style="padding: 6px 4px; text-align: left; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold;">${rowNumber++}</td>
        <td style="padding: 6px 4px; text-align: left; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; text-transform: uppercase;">${item.name}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.numberOfPieces}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.netWeight.toFixed(3)}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.stoneCharges}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.huidCharges}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${metalRateDisplay}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item?.totalPercentage}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${fineDisplay}</td>
        <td style="padding: 6px 4px; text-align: right; border-bottom: 1px solid #000;">${item?.originalTotalAmount.toFixed(2)}</td>
      </tr>
    `;
  });

  // Total row
  itemsHTML += `
    <tr style="font-weight: bold; background-color: #cbdcd8;">
      <td colspan="2" style="padding: 6px 4px; text-align: right; border-right: 1px solid #000; border-bottom: 1px solid #000; text-transform: uppercase;">Total</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalPieces}</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalNetWeight.toFixed(3)}</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalStoneCharge.toFixed(2)}</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalHuidCharge.toFixed(2)}</td>
      <td colspan="2" style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;"></td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalFineGold.toFixed(3)}</td>
      <td style="padding: 6px 4px; text-align: right; border-bottom: 1px solid #000;">${totalAmount.toFixed(2)}</td>
    </tr>
  `;

  // Calculate discount if any
  const discount = parseFloat(payments?.discount || 0);
  const grandTotal = totalAmount - discount;

  return `
    <div id="billContentWithoutGst" style="font-family: Arial, Helvetica, sans-serif; width: 100%; max-width: 950px; min-height: 650px; margin: 0 auto; background: #d7ebe7; padding: 25px; box-sizing: border-box; border: 1px solid #000; position: relative;">
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tr>
          <td colspan="2" style="text-align: center; border: 1px solid #000; padding: 6px; background: #cbdcd8; font-weight: bold; letter-spacing: 3px; font-size: 16px;">ROUGH ESTIMATE</td>
          <td style="width: 15%; text-align: center; border: 1px solid #000; padding: 6px; font-weight: bold; font-size: 12px; letter-spacing: 1px;">ORIGINAL</td>
        </tr>
        <tr>
          <td style="width: 60%; padding: 8px; border-left: 1px solid #000; border-right: 1px solid #000; font-weight: bold; font-size: 13px;">Name : ${customer?.name || "BZ - JMS"}</td>
          <td colspan="2" style="padding: 8px; border-right: 1px solid #000; font-weight: bold; font-size: 13px;">Date : ${formatDate(data.date) || "03-06-2026"}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-left: 1px solid #000; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; font-size: 13px;">Address : ${customer?.address || "-"}</td>
          <td colspan="2" style="padding: 8px; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; font-size: 13px;">Bill No. : ${data.transactionDetails?.billNo || "NA"}</td>
        </tr>
      </table>

      <table style="width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #000; background: transparent;">
        <thead>
          <tr style="background: #cbdcd8; border-bottom: 1px solid #000;">
            <th style="padding: 6px 2px; text-align: left; border-right: 1px solid #000; width: 4%;">No.</th>
            <th style="padding: 6px 4px; text-align: left; border-right: 1px solid #000; width: 28%;">Item Name</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 5%;">Pcs</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 9%;">Net Wt.</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 9%;">Stone Charge</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 9%;">HUID Charge</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 8%;">Metal Rate</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 7%;">Wastage</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 8%;">Fine</th>
            <th style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; width: 12%;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>

      ${discount > 0 ? `
      <div style="margin-top: 15px; text-align: right; font-weight: bold;">
        <div>Subtotal: ₹${totalAmount.toFixed(2)}</div>
        <div>Discount: -₹${discount.toFixed(2)}</div>
        <div style="font-size: 14px; margin-top: 5px; border-top: 1px dashed #000; display: inline-block; padding-top: 3px;">Grand Total: ₹${grandTotal.toFixed(2)}</div>
        <div style="font-size: 14px; margin-top: 5px; border-top: 1px dashed #000; display: inline-block; padding-top: 3px;">Total Fine: ${totalFineGold.toFixed(2)}gm</div>
      </div>
      ` : `
      <div style="margin-top: 15px; text-align: right; font-weight: bold;">
        <div style="font-size: 14px;">Total Amount: ₹${totalAmount.toFixed(2)}</div>
      </div>
      <div style="margin-top: 15px; text-align: right; font-weight: bold;">
        <div style="font-size: 14px;">Total Fine: ${totalFineGold.toFixed(3)}gm</div>
      </div>
      `}

      <div style="margin-top: 20px; padding: 10px; border-top: 1px solid #000;">
        <div style="font-weight: bold;">Amount in Words: ${numberToWords(grandTotal)}</div>
      </div>

      <div style="position: absolute; bottom: 30px; left: 25px; right: 25px; display: flex; justify-content: space-between; font-weight: bold; font-size: 13px;">
        <div style="text-align: left; width: 40%;">
          <span style="border-top: 1px solid #000; padding-top: 5px; display: inline-block; width: 220px;">(Customer's Signature)</span>
        </div>
        <div style="text-align: center; width: 45%;">
          <span style="border-top: 1px solid #000; padding-top: 5px; display: inline-block; width: 220px;">(Authorised Signature)</span>
        </div>
      </div>

    </div>
  `;
};

// Generate GST Bill - Handles consolidated items as single rows
const generateGSTBill = (data, items, payments, customer, advanceData) => {
  let itemsHTML = "";
  let totalGrossWeight = 0;
  let totalLessWeight = 0;
  let totalNetWeight = 0;
  let totalFineGold = 0;
  let totalAmount = 0;
  let totalPieces = 0;
  let rowNumber = 1;

  items.forEach((item, index) => {
    // Add to totals
    totalPieces += item.numberOfPieces;
    totalGrossWeight += item.grossWeight;
    totalLessWeight += item.lessWeight;
    totalNetWeight += item.netWeight;
    totalFineGold += item.fine;
    totalAmount += item.amount;

    // Format metal rate display
    const metalRateDisplay = item.metalRate > 0 ? item.metalRate.toFixed(2) : '-';

    // Format fine display - show 0 if metal rate is specified
    const fineDisplay = item.metalRate > 0 ? '0.000' : item.fine.toFixed(3);

    itemsHTML += `
      <tr>
        <td style="padding: 6px 4px; text-align: left; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold;">${rowNumber++}</td>
        <td style="padding: 6px 4px; text-align: left; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; text-transform: uppercase;">${item.name}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.numberOfPieces}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.grossWeight.toFixed(3)}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.lessWeight.toFixed(3)}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.netWeight.toFixed(3)}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${metalRateDisplay}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${item.wastage.toFixed(3)}</td>
        <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${fineDisplay}</td>
        <td style="padding: 6px 4px; text-align: right; border-bottom: 1px solid #000;">${item.amount.toFixed(2)}</td>
      </tr>
    `;
  });

  // Total row
  itemsHTML += `
    <tr style="font-weight: bold; background-color: #cbdcd8;">
      <td colspan="2" style="padding: 6px 4px; text-align: right; border-right: 1px solid #000; border-bottom: 1px solid #000; text-transform: uppercase;">Total</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalPieces}</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalGrossWeight.toFixed(3)}</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalLessWeight.toFixed(3)}</td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalNetWeight.toFixed(3)}</td>
      <td colspan="2" style="border-right: 1px solid #000; border-bottom: 1px solid #000; background: transparent;"></td>
      <td style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000;">${totalFineGold.toFixed(3)}</td>
      <td style="padding: 6px 4px; text-align: right; border-bottom: 1px solid #000;">${totalAmount.toFixed(2)}</td>
    </tr>
  `;

  // Calculate GST and totals
  const cgstRate = 0.015; // 1.5%
  const sgstRate = 0.015; // 1.5%
  const cgst = totalAmount * cgstRate;
  const sgst = totalAmount * sgstRate;
  const discount = parseFloat(payments?.discount || 0);
  const grandTotal = totalAmount + cgst + sgst - discount;

  return `
    <div id="billContent" style="font-family: Arial, Helvetica, sans-serif; width: 100%; max-width: 950px; min-height: 650px; margin: 0 auto; background: #d7ebe7; padding: 25px; box-sizing: border-box; border: 1px solid #000; position: relative;">
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tr>
          <td colspan="2" style="text-align: center; border: 1px solid #000; padding: 6px; background: #cbdcd8; font-weight: bold; letter-spacing: 3px; font-size: 16px;">TAX INVOICE</td>
          <td style="width: 15%; text-align: center; border: 1px solid #000; padding: 6px; font-weight: bold; font-size: 12px; letter-spacing: 1px;">ORIGINAL</td>
        </tr>
        <tr>
          <td style="width: 60%; padding: 8px; border-left: 1px solid #000; border-right: 1px solid #000; font-weight: bold; font-size: 13px;">Name : ${customer?.name || ""}</td>
          <td colspan="2" style="padding: 8px; border-right: 1px solid #000; font-weight: bold; font-size: 13px;">Date : ${formatDate(data.date)}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-left: 1px solid #000; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; font-size: 13px;">Address : ${customer?.address || "-"}</td>
          <td colspan="2" style="padding: 8px; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; font-size: 13px;">Bill No. : ${data.transactionDetails?.billNo || 1}</td>
        </tr>
        ${data.metaDetails?.gstNumber ? `
        <tr>
          <td colspan="3" style="padding: 8px; border-left: 1px solid #000; border-right: 1px solid #000; border-bottom: 1px solid #000; font-weight: bold; font-size: 13px;">GSTIN : ${data.metaDetails.gstNumber}</td>
        </tr>` : ''}
      </table>

      <table style="width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #000; background: transparent;">
        <thead>
          <tr style="background: #cbdcd8; border-bottom: 1px solid #000;">
            <th style="padding: 6px 2px; text-align: left; border-right: 1px solid #000; width: 4%;">No.</th>
            <th style="padding: 6px 4px; text-align: left; border-right: 1px solid #000; width: 28%;">Item Name</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 5%;">Pcs</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 9%;">Gross Wt.</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 8%;">Less Wt.</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 9%;">Net Wt.</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 8%;">Metal Rate</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 7%;">Wastage</th>
            <th style="padding: 6px 2px; text-align: center; border-right: 1px solid #000; width: 8%;">Fine</th>
            <th style="padding: 6px 4px; text-align: center; border-right: 1px solid #000; width: 12%;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>

      <div style="margin-top: 15px; margin-right: 10px; text-align: right; font-size: 12px; font-weight: bold;">
        <div>Subtotal: ₹${totalAmount.toFixed(2)}</div>
        <div>CGST (1.5%): ₹${cgst.toFixed(2)}</div>
        <div>SGST (1.5%): ₹${sgst.toFixed(2)}</div>
        ${discount > 0 ? `<div>Discount: -₹${discount.toFixed(2)}</div>` : ''}
        <div style="font-size: 14px; margin-top: 5px; border-top: 1px dashed #000; display: inline-block; padding-top: 3px;">Grand Total: ₹${grandTotal.toFixed(2)}</div>
      </div>

      <div style="margin-top: 20px; padding: 10px; border-top: 1px solid #000;">
        <div style="font-weight: bold;">Amount in Words: ${numberToWords(grandTotal)}</div>
      </div>

      <div style="position: absolute; bottom: 30px; left: 25px; right: 25px; display: flex; justify-content: space-between; font-weight: bold; font-size: 13px;">
        <div style="text-align: left; width: 40%;">
          <span style="border-top: 1px solid #000; padding-top: 5px; display: inline-block; width: 220px;">(Customer's Signature)</span>
        </div>
        <div style="text-align: center; width: 45%;">
          <span style="border-top: 1px solid #000; padding-top: 5px; display: inline-block; width: 220px;">(Authorised Signature)</span>
        </div>
      </div>
    </div>
  `;
};

// Generate bill HTML based on type
export const generateBillHTML = (billData) => {
  const { type, data, items, payments, customer, advanceData } = billData;

  return type === "gst"
    ? generateGSTBill(data, items, payments, customer, advanceData)
    : generateNonGSTBill(data, items, payments, customer, advanceData);
};

// Get CSS styles for printing
export const getBillStyles = () => `
  @media print {
    body {
      margin: 0;
      padding: 0;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    body * {
      visibility: hidden;
    }
    
    #billContent, #billContentWithoutGst {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 25px;
      background: #d7ebe7 !important;
      visibility: visible;
      border: 1px solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    #billContent *, #billContentWithoutGst * {
      visibility: visible;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    table {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    @page {
      size: A4 portrait;
      margin: 0mm;
    }
  }
  
  body {
    font-family: Arial, Helvetica, sans-serif;
    background: #e0e0e0;
    margin: 0;
    padding: 20px;
  }
`;

const billPrintUtils = {
  formatDate,
  generateTransactionBillHTML,
  parseItemObject,
  getCarat,
  numberToWords,
  generateBillHTML,
  getBillStyles,
};

export default billPrintUtils;